import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import Icon from "../../../components/AppIcon";

const SalesVsCollectionChart = ({ data }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const salesData = payload.find((p) => p.dataKey === "sales");
      const collectionsData = payload.find((p) => p.dataKey === "collections");
      const variance = salesData.value - collectionsData.value;
      const variancePercentage = ((variance / salesData.value) * 100).toFixed(
        1
      );

      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-3">{label}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Sales:</span>
              <span className="text-sm font-medium text-foreground">
                ₹{salesData.value.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Collections:
              </span>
              <span className="text-sm font-medium text-foreground">
                ₹{collectionsData.value.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-border pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Gap:</span>
                <span
                  className={`text-sm font-medium ${
                    variance > 0 ? "text-error" : "text-success"
                  }`}
                >
                  ₹{Math.abs(variance).toLocaleString()} ({variancePercentage}%)
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const getVarianceColor = (sales, collections) => {
    const variance = ((sales - collections) / sales) * 100;
    if (variance <= 10) return "var(--color-success)"; // Green - Good
    if (variance <= 25) return "var(--color-warning)"; // Amber - Caution
    return "var(--color-error)"; // Red - Alert
  };

  const getVarianceStatus = (sales, collections) => {
    const variance = ((sales - collections) / sales) * 100;
    if (variance <= 10)
      return { icon: "CheckCircle", color: "text-success", label: "Good" };
    if (variance <= 25)
      return { icon: "AlertTriangle", color: "text-warning", label: "Caution" };
    return { icon: "XCircle", color: "text-error", label: "Alert" };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Sales vs Collection Gap Analysis
          </h3>
          <p className="text-sm text-muted-foreground">
            Company-wise variance with performance indicators
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:space-x-4 mt-4 sm:mt-0">
          <div className="flex items-center space-x-2 min-w-[120px]">
            <div className="w-3 h-3 rounded-full bg-success"></div>
            <span className="text-xs text-muted-foreground">≤10% Gap</span>
          </div>
          <div className="flex items-center space-x-2 min-w-[120px]">
            <div className="w-3 h-3 rounded-full bg-warning"></div>
            <span className="text-xs text-muted-foreground">11-25% Gap</span>
          </div>
          <div className="flex items-center space-x-2 min-w-[120px]">
            <div className="w-3 h-3 rounded-full bg-error"></div>
            <span className="text-xs text-muted-foreground">&gt;25% Gap</span>
          </div>
        </div>
      </div>

      <div className="h-[300px] sm:h-80 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="company"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              angle={-50}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `₹${value / 1000}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="sales"
              fill="var(--color-primary)"
              name="Sales"
              radius={[2, 2, 0, 0]}
            />
            <Bar dataKey="collections" name="Collections" radius={[2, 2, 0, 0]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getVarianceColor(entry.sales, entry.collections)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {data /*.slice(0, 3)*/
          .map((company) => {
            const variance =
              ((company.sales - company.collections) / company.sales) * 100;
            const status = getVarianceStatus(
              company.sales,
              company.collections
            );

            return (
              <div key={company.company} className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground truncate max-w-[140px]">
                    {company.company}
                  </span>
                  <Icon name={status.icon} size={16} className={status.color} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Gap:</span>
                  <span className={`text-xs font-medium ${status.color}`}>
                    {variance.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SalesVsCollectionChart;
